import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, BookOpen, Calendar, CheckCircle2, Clock, Loader2,
  RefreshCw, Save, Search, Send, Users,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { examsApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'

// ─── helpers ────────────────────────────────────────────────────────────────

const ATTENDANCE_OPTIONS = ['present', 'absent', 'medical_leave', 'withheld']

const ATTENDANCE_LABELS = {
  present: 'Present',
  absent: 'Absent',
  medical_leave: 'Medical Leave',
  withheld: 'Withheld',
  not_entered: 'Not Entered',
}

const STATUS_LABELS = {
  pending: 'Pending',
  draft: 'Draft',
  published: 'Published',
  absent: 'Absent',
  withheld: 'Withheld',
}

const STATUS_FILTER_OPTIONS = ['all', 'pending', 'draft', 'published', 'absent', 'withheld']

const STATUS_CHIP = {
  pending:   { bg: 'bg-gray-100',    text: 'text-gray-600' },
  draft:     { bg: 'bg-amber-50',    text: 'text-amber-700' },
  published: { bg: 'bg-emerald-50',  text: 'text-emerald-700' },
  absent:    { bg: 'bg-orange-50',   text: 'text-orange-600' },
  withheld:  { bg: 'bg-red-50',      text: 'text-red-600' },
}

function getGrade(marks, total) {
  if (!total || marks === null || marks === undefined) return null
  const pct = (Number(marks) / Number(total)) * 100
  if (pct >= 90) return 'A+'
  if (pct >= 80) return 'A'
  if (pct >= 70) return 'B+'
  if (pct >= 60) return 'B'
  if (pct >= 50) return 'C'
  if (pct >= 40) return 'D'
  return 'F'
}

function isPublishedRow(row) {
  return String(row.status || '').toLowerCase() === 'published'
}

function normalizeRow(row) {
  return {
    studentId: row.studentId,
    firstName: row.firstName || '',
    lastName: row.lastName || '',
    admissionNo: row.admissionNo || '',
    attendanceStatus: row.attendanceStatus || 'not_entered',
    status: row.status || 'pending',
    marksObtained: row.marksObtained ?? null,
    percentage: row.percentage ?? null,
    grade: row.grade ?? null,
    isPassed: row.isPassed ?? null,
    feedback: row.feedback || '',
    remarks: row.remarks || '',
  }
}

// ─── StudentRow component ────────────────────────────────────────────────────

function StudentRow({ row, totalMarks, passMarks, onChange }) {
  const name = `${row.firstName} ${row.lastName}`.trim() || 'Student'
  const isPresent = row.attendanceStatus === 'present' || row.attendanceStatus === 'not_entered'
  const statusStyle = STATUS_CHIP[row.status] || STATUS_CHIP.pending

  const handleAttendance = (option) => {
    const patch = { attendanceStatus: option }
    if (option !== 'present') {
      patch.marksObtained = null
      patch.percentage = null
      patch.grade = null
      patch.isPassed = option === 'withheld' ? null : false
      patch.status = option === 'withheld' ? 'withheld' : 'absent'
    } else {
      if (row.status === 'pending') patch.status = 'draft'
    }
    onChange(row.studentId, patch)
  }

  const handleMarks = (value) => {
    const raw = value.replace(/[^0-9.]/g, '')
    const marks = raw === '' ? null : Number(raw)
    const patch = {
      marksObtained: marks,
      status: 'draft',
      attendanceStatus: 'present',
    }
    if (marks !== null && !Number.isNaN(marks) && totalMarks > 0) {
      const pct = (marks / totalMarks) * 100
      patch.percentage = Math.round(pct * 100) / 100
      patch.grade = getGrade(marks, totalMarks)
      patch.isPassed = passMarks > 0 ? marks >= passMarks : pct >= 33
    } else {
      patch.percentage = null
      patch.grade = null
      patch.isPassed = null
    }
    onChange(row.studentId, patch)
  }

  const gradeColor = row.isPassed === true
    ? '#16a34a'
    : row.isPassed === false
      ? '#dc2626'
      : '#6b7280'

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 text-sm">{name}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {row.admissionNo ? `#${row.admissionNo}` : 'No admission no'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
            {STATUS_LABELS[row.status] || row.status}
          </span>
          {row.attendanceStatus === 'present' && row.marksObtained !== null && (
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${row.isPassed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
              {row.isPassed ? 'Pass' : 'Fail'}
            </span>
          )}
        </div>
      </div>

      {/* Attendance chips */}
      <div className="flex flex-wrap gap-2">
        {ATTENDANCE_OPTIONS.map((option) => {
          const active = row.attendanceStatus === option || (row.attendanceStatus === 'not_entered' && option === 'present')
          return (
            <button
              key={option}
              type="button"
              onClick={() => handleAttendance(option)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium border-[1.5px] transition-colors ${
                active
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
              }`}
            >
              {ATTENDANCE_LABELS[option]}
            </button>
          )
        })}
      </div>

      {/* Marks + Grade */}
      <div className="flex gap-3">
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-500 mb-1.5">Marks</p>
          <input
            type="text"
            inputMode="decimal"
            value={row.marksObtained === null || row.marksObtained === undefined ? '' : String(row.marksObtained)}
            onChange={(e) => handleMarks(e.target.value)}
            disabled={!isPresent}
            placeholder={isPresent ? `Out of ${totalMarks}` : 'Not required'}
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
          />
        </div>
        <div className="w-24">
          <p className="text-xs font-medium text-gray-500 mb-1.5">Result</p>
          <div
            className="h-[42px] rounded-xl flex flex-col items-center justify-center"
            style={{ backgroundColor: `${gradeColor}18` }}
          >
            <span className="text-lg font-bold leading-none" style={{ color: gradeColor }}>
              {row.grade || '--'}
            </span>
            <span className="text-[11px] font-medium" style={{ color: gradeColor }}>
              {row.percentage !== null && row.percentage !== undefined ? `${Math.round(row.percentage)}%` : '--'}
            </span>
          </div>
        </div>
      </div>

      {/* Feedback */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1.5">Feedback</p>
        <textarea
          value={row.feedback}
          onChange={(e) => onChange(row.studentId, {
            feedback: e.target.value,
            status: row.status === 'pending' ? 'draft' : row.status,
          })}
          placeholder="Good work, keep it up"
          rows={2}
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none resize-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-100"
        />
      </div>

      {/* Remarks */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1.5">Remarks (internal)</p>
        <textarea
          value={row.remarks}
          onChange={(e) => onChange(row.studentId, {
            remarks: e.target.value,
            status: row.status === 'pending' ? 'draft' : row.status,
          })}
          placeholder="Can improve speed, needs revision"
          rows={2}
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none resize-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-100"
        />
      </div>
    </div>
  )
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function ExamResultSheetPage() {
  const { examId } = useParams()
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const fetchSheet = useCallback(async () => {
    if (!examId) return
    try {
      setLoading(true)
      const result = await examsApi.getResultSheet(examId)
      setData(result)
      setRows((result?.entries || result?.rows || []).map(normalizeRow))
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load result sheet')
    } finally {
      setLoading(false)
    }
  }, [examId])

  useEffect(() => { fetchSheet() }, [fetchSheet])

  const exam = data?.exam
  const totalMarks = Number(exam?.totalMarks || 0)
  const passMarks = Number(exam?.passMarks || 0)
  const isSessionExam = Boolean(exam?.examSessionId?._id || exam?.examSessionId?.id || exam?.examSessionId)

  const createdByName = exam?.createdBy?.firstName
    ? `${exam.createdBy.firstName} ${exam.createdBy.lastName || ''}`.trim()
    : exam?.createdBy?.name || ''
  const createdByRole = exam?.createdBy?.role || ''

  const summary = useMemo(() => {
    const total = Number(data?.summary?.totalStudents ?? data?.totalStudents ?? rows.length) || 0
    const entered = Number(
      data?.summary?.enteredResults ?? data?.summary?.enteredCount ?? data?.enteredResults ?? data?.enteredCount ?? rows.filter((r) => r.status !== 'pending').length
    ) || 0
    const published = Number(
      data?.summary?.publishedResults ?? data?.summary?.publishedCount ?? data?.publishedResults ?? data?.publishedCount ?? rows.filter(isPublishedRow).length
    ) || 0
    return { total, entered, published }
  }, [data, rows])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((row) => {
      const fullName = `${row.firstName} ${row.lastName}`.trim().toLowerCase()
      const matchSearch = !q || fullName.includes(q) || row.admissionNo.toLowerCase().includes(q)
      const matchStatus = statusFilter === 'all' || row.status === statusFilter
      return matchSearch && matchStatus
    })
  }, [rows, search, statusFilter])

  const updateRow = useCallback((studentId, patch) => {
    setRows((prev) => prev.map((row) => {
      if (row.studentId !== studentId) return row
      const next = { ...row, ...patch }
      if (patch.attendanceStatus && patch.attendanceStatus !== 'present') {
        next.marksObtained = null
        next.percentage = null
        next.grade = null
        next.isPassed = patch.attendanceStatus === 'withheld' ? null : false
        next.status = patch.attendanceStatus === 'withheld' ? 'withheld' : 'absent'
      } else if (patch.attendanceStatus === 'present' && next.status === 'pending') {
        next.status = 'draft'
      }
      if (
        next.attendanceStatus === 'present' &&
        next.marksObtained !== null &&
        !Number.isNaN(Number(next.marksObtained)) &&
        totalMarks > 0
      ) {
        const m = Number(next.marksObtained)
        const pct = (m / totalMarks) * 100
        next.percentage = Math.round(pct * 100) / 100
        next.grade = getGrade(m, totalMarks)
        next.isPassed = passMarks > 0 ? m >= passMarks : pct >= 33
      } else if (next.attendanceStatus === 'present') {
        next.percentage = null
        next.grade = null
        next.isPassed = null
      }
      return next
    }))
  }, [totalMarks, passMarks])

  const buildEntries = () =>
    rows
      .filter((r) => r.attendanceStatus !== 'not_entered' || r.marksObtained !== null || r.feedback.trim() || r.remarks.trim())
      .map((r) => ({
        studentId: r.studentId,
        attendanceStatus: r.attendanceStatus === 'not_entered' ? 'present' : r.attendanceStatus,
        marksObtained:
          (r.attendanceStatus === 'present' || r.attendanceStatus === 'not_entered')
            ? (r.marksObtained === null || r.marksObtained === '' ? null : Number(r.marksObtained))
            : null,
        feedback: r.feedback.trim(),
        remarks: r.remarks.trim(),
        status: r.status === 'pending' ? 'draft' : r.status,
      }))

  const validateEntries = (entries) => {
    if (entries.length === 0) {
      toast.error('Enter attendance or marks for at least one student.')
      return false
    }
    for (const e of entries) {
      if (e.attendanceStatus === 'present' && (e.marksObtained === null || Number.isNaN(Number(e.marksObtained)))) {
        toast.error('Marks are required for students marked present.')
        return false
      }
      if (e.attendanceStatus === 'present' && Number(e.marksObtained) > totalMarks) {
        toast.error(`Marks cannot exceed ${totalMarks}.`)
        return false
      }
    }
    return true
  }

  const handleSaveAll = async () => {
    if (!examId) return
    const entries = buildEntries()
    if (!validateEntries(entries)) return
    try {
      setSaving(true)
      await examsApi.bulkResults(examId, { entries })
      await fetchSheet()
      toast.success('Result sheet updated successfully.')
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save results')
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async () => {
    if (!examId || isSessionExam) return
    const entries = buildEntries()
    if (!validateEntries(entries)) return

    const enteredIds = rows
      .filter((r) => {
        const att = r.attendanceStatus === 'not_entered' ? 'present' : r.attendanceStatus
        if (att !== 'present') return true
        return r.marksObtained !== null && !Number.isNaN(Number(r.marksObtained))
      })
      .map((r) => r.studentId)

    const unpublished = rows.filter((r) => enteredIds.includes(r.studentId) && !isPublishedRow(r)).length
    if (unpublished === 0) {
      toast('All entered results are already published.')
      return
    }

    if (!window.confirm(`Publish results for ${unpublished} student(s)?`)) return

    try {
      setPublishing(true)
      await examsApi.bulkResults(examId, { entries })
      await examsApi.publishResults(examId, { studentIds: enteredIds })
      await fetchSheet()
      toast.success('Results are now visible to students and parents.')
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to publish results')
    } finally {
      setPublishing(false)
    }
  }

  const enteredRows = rows.filter((r) => r.status !== 'pending')
  const unpublishedCount = enteredRows.filter((r) => !isPublishedRow(r)).length
  const pendingCount = rows.length - enteredRows.length
  const allPublished = rows.length > 0 && rows.every((r) => isPublishedRow(r))

  const publishLabel = allPublished
    ? 'All Published'
    : pendingCount > 0
      ? `Publish ${unpublishedCount} Entered`
      : `Publish All (${unpublishedCount})`

  const examDateLabel = exam?.date
    ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(exam.date))
    : null

  const classLabel = exam?.classId
    ? [exam.classId.name, exam.classId.section].filter(Boolean).join(' — ')
    : null

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
      </div>
    )
  }

  if (!exam) {
    return (
      <div className="card p-8 text-center">
        <BookOpen className="w-10 h-10 mx-auto text-gray-200 mb-3" />
        <h2 className="text-lg font-semibold text-gray-900">Exam not found</h2>
        <p className="text-sm text-gray-500 mt-2">The result sheet could not be loaded.</p>
        <button onClick={() => navigate(-1)} className="btn-secondary inline-flex items-center gap-2 mt-5">
          <ArrowLeft className="w-4 h-4" /> Go Back
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={exam.title || 'Result Sheet'}
        subtitle="Enter attendance, marks, feedback, and remarks for each student. Save first, then publish when the session is complete."
        chips={[
          `${summary.total} students`,
          `${summary.entered} entered`,
          `${summary.published} published`,
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={fetchSheet} className="btn-secondary p-2" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => navigate(-1)} className="btn-secondary inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      {/* Exam info card */}
      <div className="card p-5 space-y-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{exam.title}</h3>
          {(classLabel || exam.subjectId?.name) && (
            <p className="text-sm text-gray-500 mt-0.5">
              {[classLabel, exam.subjectId?.name].filter(Boolean).join(' · ')}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            {[
              totalMarks ? `${totalMarks} marks total` : null,
              passMarks ? `pass ${passMarks}` : null,
              exam.examType || 'theory',
              exam.status,
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {examDateLabel && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-100 rounded-full px-2.5 py-1">
              <Calendar className="w-3 h-3" /> {examDateLabel}
            </span>
          )}
          {createdByName && (
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${createdByRole === 'admin' || createdByRole === 'super_admin' ? 'bg-primary-50 text-primary-700' : 'bg-blue-50 text-blue-700'}`}>
              By: {createdByName}
            </span>
          )}
          {isSessionExam && (
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
              Publish: Admin session-wise
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: Users, label: 'Students', value: summary.total || rows.length, color: 'text-primary-600', bg: 'bg-primary-50' },
          { icon: Clock, label: 'Entered', value: summary.entered, color: 'text-amber-600', bg: 'bg-amber-50' },
          { icon: CheckCircle2, label: 'Published', value: summary.published, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(({ icon: Icon, label, value, color, bg }) => (
          <div key={label} className="card p-4">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-xl ${bg} ${color} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-xl font-bold text-gray-900">{value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar: search + filter + session note + actions */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or admission no"
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {STATUS_FILTER_OPTIONS.map((opt) => {
            const active = statusFilter === opt
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setStatusFilter(opt)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium border-[1.5px] capitalize transition-colors ${
                  active
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                }`}
              >
                {opt}
              </button>
            )
          })}
        </div>

        {isSessionExam && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-1">
            <p className="text-xs font-semibold text-gray-700">Session-linked exam</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Save marks here. Admin will publish results session-wise for this class after all subject result sheets are complete.
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save All
          </button>
          {!isSessionExam && !allPublished && (
            <button
              onClick={handlePublish}
              disabled={publishing || unpublishedCount === 0}
              className="btn-secondary flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {publishLabel}
            </button>
          )}
        </div>
      </div>

      {/* Student rows */}
      {filteredRows.length === 0 ? (
        <div className="card py-12 text-center text-gray-400">
          <Search className="w-8 h-8 mx-auto mb-3 text-gray-200" />
          <p className="text-sm">No students match this filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredRows.map((row) => (
            <StudentRow
              key={row.studentId}
              row={row}
              totalMarks={totalMarks}
              passMarks={passMarks}
              onChange={updateRow}
            />
          ))}
        </div>
      )}
    </div>
  )
}
